<template>
    <v-card outlined>
        <v-data-table
                class="elevation-0"
                :headers="headers"
                :items="products"
                :items-per-page="15"
                :footer-props="dataTableFooterProps"
                :search="dataTableSearch"
                :custom-filter="dataTableCustomFilter"
                :loading-text="$t('common.loading_table_data')"
        >
            <template v-slot:top>
                <v-toolbar flat color="white">
                    <v-text-field
                            v-model="dataTableSearch"
                            prepend-inner-icon="mdi-magnify"
                            :placeholder="$t('common.filter')"
                            outlined
                            dense
                            hide-details
                    />
                </v-toolbar>
            </template>
            <template v-slot:item.thumbnail="{ item }">
                <v-avatar size="50" tile>
                    <v-img :src="item.image_url"/>
                </v-avatar>
            </template>
            <template v-slot:item.in_stock="{ item }">
                {{ item.in_stock }}
                {{ $t("products.pieces") }}
            </template>
            <template v-slot:item.quantity="{ item }">
                <div style="max-width: 100px">
                    <v-text-field
                            v-if="!readonly"
                            type="number"
                            :value="item.quantity"
                            :min="1"
                            :max="availableQuantity(item)"
                            :error="item.quantity > availableQuantity(item) || item.quantity < 1"
                            dense
                            hide-details
                            outlined
                            @change="onQuantityChange($event, item)"
                            @input="onQuantityChange($event, item)"
                    />
                    <template v-else>
                        {{ item.quantity }}
                        {{ $t("products.pieces") }}
                    </template>
                </div>
            </template>
            <template v-slot:item.actions="{ item }">
                <div class="text-no-wrap">
                    <v-btn icon @click="onDelete(item)" color="error" :disabled="readonly">
                        <v-icon>mdi-trash-can</v-icon>
                    </v-btn>
                </div>
            </template>
            <template v-slot:footer.prepend>
                {{
                    $tc('shopping_cart.products_in_shopping_cart', productsInCart, {n: productsInCart})
                    + " / "
                    + $tc('shopping_cart.total_products_in_shopping_cart', totalProductsInCart, {n: totalProductsInCart})
                }}
            </template>
        </v-data-table>
    </v-card>
</template>

<script>
import { EventBus } from "@/eventBus";
import ShoppingCartMixin from "@/mixins/ShoppingCartMixin";
import SnackbarMixin from "@/mixins/SnackbarMixin";
import DataTableMixin from "@/mixins/DataTableMixin";

export default {
    name: 'ShoppingCartComponent',
    mixins: [ShoppingCartMixin, SnackbarMixin, DataTableMixin],
    props: {
        readonly: {
            type: Boolean,
            default: () => false
        },
        userId: {
            type: Number,
            required: true
        },
        warehouseId: {
            type: Number,
            required: true
        },
        storeModule: {
            type: String,
            required: false,
            default: () => "shoppingCart"
        },
        stockAllowance: {
            type: Boolean,
            default: () => false
        }
    },
    data: () => ({
        // items
        products: [],
        // dialogs
        addToCartDialog: false,
        product: null,
        // products in cart
        productsInCart: 0,
        totalProductsInCart: 0
    }),
    computed: {
        headers () {
            return [
                {
                    text: this.$t('shopping_cart.thumbnail'),
                    value: 'thumbnail',
                    sortable: false
                },
                {
                    text: this.$t('shopping_cart.name'),
                    value: 'name',
                    sortable: true
                },
                {
                    text: this.$t('shopping_cart.model'),
                    value: 'model',
                    sortable: true
                },
                {
                    text: this.$t('shopping_cart.in_stock'),
                    value: 'in_stock',
                    sortable: false,
                },
                {
                    text: this.$t('shopping_cart.quantity'),
                    value: 'quantity',
                    sortable: false,
                },
                {
                    text: '',
                    value: 'actions',
                    sortable: false,
                    align: 'end'
                }
            ];
        }
    },
    methods: {
        availableQuantity(product) {
            const inStock = Number(product.in_stock);
            const originalQuantity = this.stockAllowance
                ? Number(product.original_quantity || 0)
                : 0;
            const availableQuantity = inStock + originalQuantity;
            return Number.isSafeInteger(inStock)
                && inStock >= 0
                && Number.isSafeInteger(originalQuantity)
                && originalQuantity >= 0
                && Number.isSafeInteger(availableQuantity)
                ? availableQuantity
                : 0;
        },
        fetchProducts () {
            this.products = this.getProductsInCart(this.userId, this.warehouseId, this.storeModule);
        },
        onUpdate (product) {
            this.product = product;
            this.addToCartDialog = true;
        },
        onDelete (product, withConfirmation = true) {
            if (withConfirmation) {
                if (!confirm(this.$t('confirm.delete_product_from_shopping_cart', { product: product.name }))) {
                    return;
                }
            }

            this.removeProductFromCart(this.userId, this.warehouseId, product.external_id, this.storeModule);
            this.fetchProducts();

            if (this.products.length === 0) {
                this.$router.go();
            }

            EventBus.$emit('close-product-dialogs');
            EventBus.$emit('refresh-shopping-cart-indicator');
        },
        onQuantityChange (quantity, product) {
            this.addProductToShoppingCart(this.userId, this.warehouseId, product, quantity, this.storeModule);
            EventBus.$emit('refresh-shopping-cart-indicator');
        }
    },
    created () {
        let userId = this.$store.getters['identity/getUserId'];
        if (userId === null) {
            this.snackbarError(this.$t('snackbar.identity_check_failed'));
            return;
        }

        this.fetchProducts();

        EventBus.$on('close-product-dialogs', () => {
            this.addToCartDialog = false;
        });

        EventBus.$on('remove-from-shopping-cart', (product) => {
            this.onDelete(product, false);
        });

        // products
        this.productsInCart = this.getNumberOfProductsInCart(userId, this.warehouseId);
        this.totalProductsInCart = this.getTotalNumberOfProductsInCart(userId, this.warehouseId);

        // refresh shopping cart indicator when some product is added to cart
        EventBus.$on('refresh-shopping-cart-indicator', () => {
            this.productsInCart = this.getNumberOfProductsInCart(userId, this.warehouseId);
            this.totalProductsInCart = this.getTotalNumberOfProductsInCart(userId, this.warehouseId);
        });
    }
}
</script>
